"use client"

import { useState, useEffect } from "react"
import { useAuth0 } from '@auth0/auth0-react';

const useBudget = (actionId, action) => {
  console.log("useBudget hook initialized with actionId:", actionId)

  const [budget, setBudget] = useState({
    amount: 0,
    justification: "",
    breakdown: [],
  })
  const [suggestions, setSuggestions] = useState([])
  const [comments, setComments] = useState([])
  const [newSuggestion, setNewSuggestion] = useState({
    amount: "",
    justification: "",
    breakdown: [{ item: "", cost: "" }],
  })
  const [newComment, setNewComment] = useState("")
  const [activeTab, setActiveTab] = useState("current")
  const [loading, setLoading] = useState(true)
  const { getAccessTokenSilently } = useAuth0();
  
  const apiUrl = process.env.REACT_APP_API_URL || window.location.origin
  console.log("API URL:", apiUrl)

  const fetchBudgetData = async () => {
    console.log("fetchBudgetData called with actionId:", actionId)

    if (!actionId) {
      console.log("No actionId provided, skipping fetch")
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                }
            });
      console.log("Token exists:", !!token)

      // Fetch the current budget
      const budgetUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/budget`
      console.log("Fetching budget from:", budgetUrl)

      const budgetResponse = await fetch(budgetUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Budget response status:", budgetResponse.status)

      if (budgetResponse.ok) {
        const budgetData = await budgetResponse.json()
        console.log("Budget data received:", budgetData)
        setBudget(budgetData || { amount: action?.fundingAllocated || 0, justification: "", breakdown: [] })
      } else {
        console.error("Budget response not ok:", budgetResponse.status, budgetResponse.statusText)
      }

      // Fetch suggestions
      const suggestionsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/budget/suggestions`
      console.log("Fetching suggestions from:", suggestionsUrl)

      const suggestionsResponse = await fetch(suggestionsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Suggestions response status:", suggestionsResponse.status)

      if (suggestionsResponse.ok) {
        const suggestionsData = await suggestionsResponse.json()
        console.log("Suggestions data received:", suggestionsData)
        setSuggestions(suggestionsData || [])
      } else {
        console.error("Suggestions response not ok:", suggestionsResponse.status, suggestionsResponse.statusText)
      }

      // Fetch comments
      const commentsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/budget/comments`
      console.log("Fetching comments from:", commentsUrl)

      const commentsResponse = await fetch(commentsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Comments response status:", commentsResponse.status)

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json()
        console.log("Comments data received:", commentsData)
        setComments(commentsData || [])
      } else {
        console.error("Comments response not ok:", commentsResponse.status, commentsResponse.statusText)
      }
    } catch (error) {
      console.error("Error fetching budget data:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddBreakdownItem = () => {
    setNewSuggestion((prev) => ({
      ...prev,
      breakdown: [...prev.breakdown, { item: "", cost: "" }],
    }))
  }

  const handleRemoveBreakdownItem = (index) => {
    setNewSuggestion((prev) => ({
      ...prev,
      breakdown: prev.breakdown.filter((_, i) => i !== index),
    }))
  }

  const handleBreakdownItemChange = (index, field, value) => {
    setNewSuggestion((prev) => {
      const updatedBreakdown = [...prev.breakdown]
      updatedBreakdown[index] = {
        ...updatedBreakdown[index],
        [field]: value,
      }
      return {
        ...prev,
        breakdown: updatedBreakdown,
      }
    })
  }

  const updateNewSuggestion = (field, value) => {
    setNewSuggestion((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmitSuggestion = async () => {
    console.log("handleSubmitSuggestion called with:", newSuggestion)

    if (!newSuggestion.amount || !newSuggestion.justification) {
      console.log("Missing required fields, skipping submit")
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/modules/budget/suggestions`

      console.log("Submitting suggestion to:", url)
      console.log("Suggestion data:", newSuggestion)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: JSON.stringify(newSuggestion) }),
      })

      console.log("Submit suggestion response status:", response.status)

      if (response.ok) {
        setNewSuggestion({
          amount: "",
          justification: "",
          breakdown: [{ item: "", cost: "" }],
        })
        await fetchBudgetData()
      } else {
        console.error("Failed to submit suggestion:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting suggestion:", error)
    }
  }

  const handleSubmitComment = async () => {
    console.log("handleSubmitComment called with:", newComment)

    if (!newComment.trim()) {
      console.log("Empty comment, skipping submit")
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/modules/budget/comments`

      console.log("Submitting comment to:", url)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newComment }),
      })

      console.log("Submit comment response status:", response.status)

      if (response.ok) {
        setNewComment("")
        await fetchBudgetData()
      } else {
        console.error("Failed to submit comment:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting comment:", error)
    }
  }

  const handleVote = async (suggestionId, voteType) => {
    console.log("handleVote called with:", suggestionId, voteType)

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/suggestions/${suggestionId}/vote`

      console.log("Submitting vote to:", url)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ voteType }),
      })

      console.log("Vote response status:", response.status)

      if (response.ok) {
        await fetchBudgetData()
      } else {
        console.error("Failed to submit vote:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting vote:", error)
    }
  }

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "N/A"
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  useEffect(() => {
    console.log("useEffect triggered with actionId:", actionId)
    fetchBudgetData()
  }, [actionId])

  console.log("useBudget hook returning state:", {
    budgetAmount: budget.amount,
    suggestionsCount: suggestions.length,
    commentsCount: comments.length,
    loading,
  })

  return {
    // State
    budget,
    suggestions,
    comments,
    newSuggestion,
    newComment,
    activeTab,
    loading,

    // Actions
    setActiveTab,
    setNewComment,
    updateNewSuggestion,
    handleAddBreakdownItem,
    handleRemoveBreakdownItem,
    handleBreakdownItemChange,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote,
    formatCurrency,
    fetchBudgetData,
  }
}

export default useBudget
